<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.paymentChannelsTitle') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.paymentChannelsSubtitle') }}</p>
        </div>
        <div>
          <router-link
            :to="{ name: 'CreatePayment' }"
            class="btn btn-primary"
          >
            <i class="mdi mdi-plus mr-1"></i>
            {{ $t('superadmin.createPaymentMethod') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Payment Table Card -->
    <div class="col-12 grid-margin">
      <div class="card">
        <div class="card-body">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div class="search-box" style="min-width: 320px; max-width: 440px; width: 100%;">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('common.search')"
                />
                <button v-if="search" class="btn btn-outline-secondary" type="button" @click="search = ''">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-medium">{{ filteredPayments.length }}</span>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th style="width: 50px;">No</th>
                  <th>{{ $t('superadmin.paymentMethodName') }}</th>
                  <th style="width: 200px;">{{ $t('superadmin.baseFee') }}</th>
                  <th style="width: 140px;">{{ $t('common.status') }}</th>
                  <th class="text-right" style="width: 160px;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in paginatedPayment" :key="item.id || index">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-md mr-3 d-flex align-items-center justify-content-center"
                        style="width: 38px; height: 38px; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.25); border-radius: 8px; flex-shrink: 0;"
                      >
                        <i class="mdi mdi-credit-card-outline text-info" style="font-size: 20px;"></i>
                      </div>
                      <div>
                        <router-link
                          :to="{ name: 'DetailPayment', params: { id: item.id }}"
                          class="text-white font-weight-semibold text-decoration-none"
                        >
                          {{ item.name }}
                        </router-link>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="text-success font-weight-bold">
                      {{ formatRupiah(item.price) }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="item.status === 'Active' ? 'badge-outline-success' : 'badge-outline-secondary'"
                    >
                      {{ item.status || 'Active' }}
                    </span>
                  </td>
                  <td class="text-right text-nowrap">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailPayment', params: { id: item.id }}"
                        class="btn btn-secondary btn-sm"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye mr-1"></i>{{ $t('common.view') }}
                      </router-link>
                      <router-link
                        :to="{ name: 'EditPayment', params: { id: item.id }}"
                        class="btn btn-outline-secondary btn-sm ml-1"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil mr-1"></i>{{ $t('common.edit') }}
                      </router-link>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedPayment.length === 0">
                  <td colspan="5" class="text-center py-5">
                    <EmptyState
                      icon="mdi-credit-card-outline"
                      :title="$t('superadmin.noPaymentsFound')"
                      :description="$t('superadmin.noPaymentsDesc')"
                      :actionText="$t('superadmin.createPaymentMethod')"
                      actionTo="/payment/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredPayments.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredPayments.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPayment } from '../../services/paymentService';
import moment from 'moment';
import PaginationComponents from '../../components/PaginationComponents.vue';
import EmptyState from '@/components/EmptyState.vue';

export default {
  name: 'PaymentListView',
  components: {
    PaginationComponents,
    EmptyState
  },
  data() {
    return {
      payment: [],
      search: '',
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
    filteredPayments() {
      if (!this.search) return this.payment;
      const q = this.search.toLowerCase();
      return this.payment.filter(p => (p.name || '').toLowerCase().includes(q));
    },
    paginatedPayment() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPayments.slice(start, end);
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD MMMM YYYY');
    },
    fetchPayment() {
      getPayment()
        .then(response => {
          this.payment = Array.isArray(response) ? response : [];
        })
        .catch(error => {
          console.error('Error fetching payments:', error);
        });
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    formatRupiah(number) {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(number || 0);
    }
  },
  mounted() {
    this.fetchPayment();
  },
};
</script>